import { useRef, useEffect, useCallback } from "react";
import { Button } from "./ui/button";

export default function SignaturePad({ value, onChange, disabled, className = "", footerContent = null }) {
  const canvasRef = useRef(null);
  // Input can arrive before React commits a state update. Keep the active
  // pointer synchronous so even the first rapid touch/stylus move is drawn.
  const activePointerRef = useRef(null);
  const hasDrawnRef = useRef(false);
  const lastPosRef = useRef(null);
  const lastRenderedValueRef = useRef("");

  const getCanvasRectSize = useCallback((canvas) => {
    const rect = canvas.getBoundingClientRect();
    return { width: rect.width, height: Math.min(120, rect.height || 120) };
  }, []);

  const getPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const dpr = window.devicePixelRatio || 1;
    return {
      x: (e.clientX - rect.left) * (canvas.width / dpr / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / dpr / rect.height),
    };
  }, []);

  const draw = useCallback((from, to) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    hasDrawnRef.current = true;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  }, []);

  const drawStoredSignature = useCallback((ctx, img, targetWidth, targetHeight) => {
    if (!ctx || !img || !targetWidth || !targetHeight) return;
    ctx.clearRect(0, 0, targetWidth, targetHeight);
    // Render stored signatures back into the exact same signature box.
    // This keeps the original stroke position stable instead of re-centering it.
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  }, []);

  const startDrawing = useCallback(
    (e) => {
      if (disabled || activePointerRef.current !== null || e.button !== 0) return;
      e.preventDefault();
      const pos = getPoint(e);
      if (pos) {
        lastPosRef.current = pos;
        activePointerRef.current = e.pointerId;
        // Continue the stroke and receive its end even outside the box.
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    },
    [disabled, getPoint]
  );

  const moveDrawing = useCallback(
    (e) => {
      if (disabled || activePointerRef.current !== e.pointerId) return;
      e.preventDefault();
      const pos = getPoint(e);
      if (pos && lastPosRef.current) {
        draw(lastPosRef.current, pos);
        lastPosRef.current = pos;
      }
    },
    [disabled, getPoint, draw]
  );

  const emitChange = useCallback(() => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!hasDrawnRef.current) {
      lastRenderedValueRef.current = "";
      onChange?.("");
      return;
    }
    // Keep the original drawing area intact so the signature does not
    // change its local coordinate system after saving/reloading.
    const dataUrl = canvas.toDataURL("image/png");
    lastRenderedValueRef.current = dataUrl;
    onChange?.(dataUrl);
  }, [disabled, onChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rectSize = getCanvasRectSize(canvas);
    const targetWidth = Math.max(1, Math.round(rectSize.width * dpr));
    const targetHeight = Math.max(1, Math.round(rectSize.height * dpr));
    const isSameValue =
      Boolean(value) &&
      typeof value === "string" &&
      value.startsWith("data:image/") &&
      value === lastRenderedValueRef.current &&
      hasDrawnRef.current;

    // Keep the exact live strokes when we receive the same data back from parent state.
    if (isSameValue && canvas.width === targetWidth && canvas.height === targetHeight) {
      return;
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.strokeStyle = "#1D1D1F";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.clearRect(0, 0, rectSize.width, rectSize.height);

    if (
      value &&
      typeof value === "string" &&
      value.startsWith("data:image/")
    ) {
      hasDrawnRef.current = true;
      const img = new Image();
      img.onload = () => {
        drawStoredSignature(ctx, img, rectSize.width, rectSize.height);
        lastRenderedValueRef.current = value;
      };
      img.src = value;
    } else {
      hasDrawnRef.current = false;
      lastRenderedValueRef.current = "";
    }
  }, [drawStoredSignature, getCanvasRectSize, value]);

  const finishDrawing = useCallback((e) => {
    if (activePointerRef.current !== e.pointerId) return;
    activePointerRef.current = null;
    lastPosRef.current = null;
    const canvas = canvasRef.current;
    if (canvas?.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    emitChange();
  }, [emitChange]);

  const clear = useCallback(() => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    hasDrawnRef.current = false;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Clear the whole bitmap even if the responsive box changed size since
    // drawing (for example after rotating a phone).
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    ctx.strokeStyle = "#1D1D1F";
    ctx.lineWidth = 2;
    lastRenderedValueRef.current = "";
    onChange?.("");
  }, [disabled, onChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <div
        className="relative rounded-xl border border-[#E5E5EA] bg-[#FAFAFB] overflow-hidden touch-none"
        style={{ height: 120 }}
      >
        <canvas
          ref={canvasRef}
          className="block h-full w-full cursor-crosshair"
          style={{ height: 120, touchAction: "none" }}
          width={400}
          height={120}
          onPointerDown={startDrawing}
          onPointerMove={moveDrawing}
          onPointerUp={finishDrawing}
          onPointerCancel={finishDrawing}
          onLostPointerCapture={finishDrawing}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clear} disabled={disabled} className="btn-secondary">
          {"L\u00f6schen"}
        </Button>
        {footerContent}
      </div>
    </div>
  );
}
